// Generic Pattern A faceted filter cascade (ADR-0045).
// Fetches JSON from a filter_options endpoint when a parent select changes,
// then updates dependent Slim Select instances or plain <select> elements.
//
// Sibling: `faceted_search_controller.js` + the `FacetedSearchable` concern
// handle the SEARCH family — entity search/create forms that resolve or capture
// a value. See ADR-0059 for the rule on which family to use.
//
// Usage:
//   data-controller="faceted-filter"
//   data-faceted-filter-url-value="/path/to/filter_options"
//
// On each triggering parent select:
//   data-action="change->faceted-filter#changed"
//
// On each dependent element (slim or plain select):
//   data-faceted-filter-target="dependent"
//   data-faceted-filter-response-key="brands"
//   data-faceted-filter-placeholder="All Brands"
//   data-faceted-filter-empty-text="Select a client first"

import { Controller } from "@hotwired/stimulus"
import { whenSlimReady } from "../lib/slim_options"

export default class extends Controller {
  static targets = ["dependent"]
  static values = { url: String }

  changed() {
    const form = this.element.closest("form") || this.element
    const params = new URLSearchParams(new FormData(form))
    const url = `${this.urlValue}?${params.toString()}`

    fetch(url, { headers: { "Accept": "application/json" } })
      .then(r => r.json())
      .then(data => {
        this.dependentTargets.forEach(el => {
          const key = el.dataset.facetedFilterResponseKey
          if (key && data[key] !== undefined) {
            whenSlimReady(this.application, el, {
              onReady: (slim) => this.#updateSlim(el, slim, data[key]),
              onNoSlim: () => this.#updateSelect(el, data[key])
            })
          }
        })
      })
  }

  // Waiting for Slim to initialise is shared with faceted_search_controller.js
  // (lib/slim_options.js, ADR-0059); the placeholder/empty handling is ours.
  #updateSlim(el, slim, items) {
    if (items.length > 0) {
      const placeholder = el.dataset.facetedFilterPlaceholder || "All"
      slim.setData([{ placeholder: true, text: placeholder, value: "" }, ...items])
      slim.enable()
    } else {
      const emptyText = el.dataset.facetedFilterEmptyText || "No options available"
      slim.setData([{ text: emptyText, value: "" }])
      slim.disable()
    }
  }

  #updateSelect(el, items) {
    const placeholder = el.dataset.facetedFilterPlaceholder || "All"
    const emptyText = el.dataset.facetedFilterEmptyText || "No options available"
    const previousValue = el.value
    el.innerHTML = ""
    if (items.length > 0) {
      el.add(new Option(placeholder, ""))
      items.forEach(item => el.add(new Option(item.text, item.value)))
      el.disabled = false
      if (previousValue && [...el.options].some(o => o.value === previousValue)) {
        el.value = previousValue
      }
    } else {
      el.add(new Option(emptyText, ""))
      el.disabled = true
    }
  }
}
